import { useEffect, useState } from 'react';
import { Check, LoaderCircle, X } from 'lucide-react';
import { TestField, TestSelect } from './TestAdminUi';
import { errorMessage } from '../lib/testAdminFormat';
import { testApi, type TestRecord } from '../lib/testApi';

type CourseOption = { objectId: string; CourseTitle?: string };

export function AddQuestionsToTestModal({
  courses,
  questionCount,
  initialCourseId = '',
  saving = false,
  onClose,
  onSubmit,
}: {
  courses: CourseOption[];
  questionCount: number;
  initialCourseId?: string;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (testId: string) => Promise<void>;
}) {
  const [courseId, setCourseId] = useState(initialCourseId);
  const [search, setSearch] = useState('');
  const [tests, setTests] = useState<TestRecord[]>([]);
  const [testId, setTestId] = useState('');
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!courseId) {
      setTests([]);
      setTestId('');
      setHasMore(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError('');
    setTestId('');
    setSkip(0);
    testApi.listTests({ kind: 'tests', courseId, search, order: '-updatedAt', skip: 0, limit: 50 })
      .then((page) => {
        if (!active) return;
        setTests(page.results);
        setHasMore(page.hasMore);
        setSkip(page.skip + page.results.length);
      })
      .catch((reason) => { if (active) setError(errorMessage(reason, 'Tests for this course could not be loaded.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [courseId, search]);

  const loadMore = async () => {
    if (!courseId || loading || !hasMore) return;
    setLoading(true);
    setError('');
    try {
      const page = await testApi.listTests({ kind: 'tests', courseId, search, order: '-updatedAt', skip, limit: 50 });
      setTests((current) => [...current, ...page.results]);
      setHasMore(page.hasMore);
      setSkip(page.skip + page.results.length);
    } catch (reason) {
      setError(errorMessage(reason, 'More tests could not be loaded.'));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (testId) await onSubmit(testId);
  };

  return <div className="test-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className="test-add-questions-dialog" role="dialog" aria-modal="true" aria-labelledby="add-questions-title">
      <header className="test-add-questions-heading">
        <div><span className="test-kicker">Question assignment</span><h2 id="add-questions-title">Add questions to a test</h2><p>{questionCount.toLocaleString('en-IN')} selected · existing mappings are skipped</p></div>
        <button type="button" className="test-add-questions-close" onClick={onClose} disabled={saving} aria-label="Close dialog"><X size={18} /></button>
      </header>
      <form onSubmit={submit}>
        <TestSelect label="Course" value={courseId} onChange={setCourseId} options={courses.map((course) => ({ value: course.objectId, label: course.CourseTitle || 'Untitled course' }))} required />
        <TestField label="Find a test" value={search} onChange={setSearch} placeholder="Search tests in this course" />
        <TestSelect label="Test" value={testId} onChange={setTestId} options={tests.map((test) => ({ value: test.objectId, label: test.TestTitle || 'Untitled test' }))} required />
        {loading && <div className="test-add-questions-status"><LoaderCircle size={15} />Loading tests for this course…</div>}
        {!loading && courseId && !tests.length && !error && <p className="test-add-questions-empty">No tests found for this course{search ? ' matching that search' : ''}.</p>}
        {hasMore && <button type="button" className="test-add-questions-more" onClick={() => void loadMore()} disabled={loading}>Load more tests</button>}
        {error && <p className="test-add-questions-error" role="alert">{error}</p>}
        <footer className="test-add-questions-actions"><button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button><button className="btn btn-primary" type="submit" disabled={!testId || saving}>{saving ? <><LoaderCircle size={15} />Adding…</> : <><Check size={15} />Add to test</>}</button></footer>
      </form>
    </section>
  </div>;
}
